import type { FC } from "react";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { Autocomplete, Box, TextField } from "@mui/material";
import { useTranslation } from "@privacy/translations";
import { useMutation } from "@tanstack/react-query";
import type { Agreement, GetPartnerByIdResponse } from "../../api/partners";
import * as FormDialog from "../../shared/FormDialog";
import type { AutocompleteOptionType } from "../../shared/Autocomplete";
import {
  AUTOCOMPLETE_LOADING_OPTION,
  AutocompleteOption,
  createFilterAutocompleteOption,
  CreateNewAutocompleteOption,
} from "../../shared/Autocomplete";
import { CreateAndUpdateAgreementFormDialog } from "../../agreements/CreateOrUpdateAgreementFormDialog";
import { isAgreementExpired } from "../../agreements/isAgreementExpired";
import { ValidityText } from "../../agreements/AgreementDate";
import { InfoIcon } from "../../shared/icons";
import type { MultipleAgreementValue, SelectedAgreement } from "../types";

type AgreementOption = AutocompleteOptionType & {
  partnerId?: string;
  isNew?: boolean;
  isUnavailable?: boolean;
  partnerName?: string;
  validityText?: string;
  isExpired?: boolean;
};

type FormValues = { selectedOption: AgreementOption | null };

type AddAgreementDialogProps = {
  onClose: () => void;
  onSubmit: (agreement: SelectedAgreement) => Promise<unknown>;
  partners: Array<GetPartnerByIdResponse>;
  isPartnersLoading: boolean;
  isPartnersError: boolean;
  selectedAgreements: MultipleAgreementValue;
  canCreateAgreement: boolean;
};

export const AddAgreementDialog: FC<AddAgreementDialogProps> = ({
  onClose,
  onSubmit,
  partners,
  isPartnersLoading,
  isPartnersError,
  selectedAgreements,
  canCreateAgreement,
}) => {
  const { t, formatDate } = useTranslation();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const submitMutation = useMutation({ mutationFn: onSubmit });
  const { handleSubmit, control, setFocus } = useForm<FormValues>({ defaultValues: { selectedOption: null } });

  FormDialog.useAutoFocusField(setFocus, "selectedOption");

  const getValidityText = (agreement: Agreement, isExpired: boolean) => {
    if (!agreement.validUntil) {
      return t("System.SuppliersAgreements.Agreement.NoDate");
    }
    const date = formatDate(agreement.validUntil, {});
    if (isExpired) {
      return t("System.SuppliersAgreements.Agreement.Expired", { date });
    }
    return t("System.SuppliersAgreements.Agreement.ValidUntil", { date });
  };

  const partnerAgreementOptions: Array<AgreementOption> = partners.flatMap(partner =>
    partner.agreementDocuments
      .filter(agreement => !selectedAgreements.some(selected => selected.agreementId === agreement.id))
      .map(agreement => {
        const isExpired = isAgreementExpired(agreement);
        const validityText = getValidityText(agreement, isExpired);
        return {
          value: agreement.id,
          label: agreement.title,
          description: `${partner.name} · ${validityText}`,
          partnerId: partner.id,
          partnerName: partner.name,
          validityText,
          isExpired,
        };
      })
  );
  const options: Array<AgreementOption> = isPartnersLoading ? [AUTOCOMPLETE_LOADING_OPTION] : partnerAgreementOptions;

  return (
    <FormDialog.Dialog isOpen={true}>
      {isCreateDialogOpen && (
        <CreateAndUpdateAgreementFormDialog
          partners={partners}
          showTypeField
          title={t("DynamicForm.MultipleAgreementInput.NewAgreement.Title")}
          description={<NewAgreementNote />}
          onClose={() => setIsCreateDialogOpen(false)}
          onSaved={async (agreementId, partnerId) => {
            await onSubmit({ partnerId, agreementId });
            onClose();
          }}
        />
      )}
      <FormDialog.Title>{t("DynamicForm.MultipleAgreementInput.DialogTitle")}</FormDialog.Title>
      <FormDialog.Form
        onSubmit={handleSubmit(({ selectedOption }) => {
          if (!selectedOption?.partnerId) {
            return;
          }
          submitMutation.mutate(
            { partnerId: selectedOption.partnerId, agreementId: selectedOption.value },
            { onSuccess: onClose }
          );
        })}
      >
        <FormDialog.Content>
          <FormDialog.FormError isError={submitMutation.isError || isPartnersError} />
          <FormDialog.Fields>
            <Controller
              control={control}
              name="selectedOption"
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormDialog.Field errorMessage={fieldState.error && t("Input.Validation.Required")}>
                  <FormDialog.Label htmlFor="agreement">
                    {t("DynamicForm.MultipleAgreementInput.AgreementLabel")}
                  </FormDialog.Label>
                  <Autocomplete
                    noOptionsText={t("Partner.Agreement.NoAgreementsYet")}
                    {...field}
                    value={field.value}
                    onChange={(_, selectedOption) => {
                      if (selectedOption?.isNew) {
                        setIsCreateDialogOpen(true);
                        return;
                      }
                      field.onChange(selectedOption);
                    }}
                    openOnFocus
                    clearOnBlur
                    clearOnEscape
                    disableClearable={Boolean(field.value)}
                    blurOnSelect
                    id="agreement"
                    options={options}
                    getOptionLabel={option => option.label}
                    getOptionKey={option => option.value || option.label}
                    isOptionEqualToValue={(option, selected) => option.value === selected.value}
                    getOptionDisabled={option => Boolean(option.isUnavailable)}
                    groupBy={option => option.partnerId ?? ""}
                    renderGroup={params => (
                      <Box
                        component="li"
                        key={params.key}
                        sx={
                          params.group
                            ? { "&:not(:first-of-type)": { borderTop: theme => `1px solid ${theme.palette.divider}` } }
                            : undefined
                        }
                      >
                        <Box component="ul" sx={{ p: 0 }}>
                          {params.children}
                        </Box>
                      </Box>
                    )}
                    filterOptions={(sourceOptions, params) => {
                      const filtered = createFilterAutocompleteOption(sourceOptions, params);
                      if (!canCreateAgreement || isPartnersLoading || isPartnersError) {
                        return filtered;
                      }
                      const noAgreementsOption: AgreementOption = {
                        value: "",
                        label: t("Partner.Agreement.NoAgreementsYet"),
                        isUnavailable: true,
                      };
                      const createNewOption: AgreementOption = {
                        value: "",
                        label: t("MultiAutocomplete.CreateNewOption.No.Input.Label"),
                        isNew: true,
                      };
                      const agreementOptions = filtered.length > 0 ? filtered : [noAgreementsOption];
                      return [...agreementOptions, createNewOption];
                    }}
                    renderOption={(props, option) => {
                      const { key, ...optionProps } = props;
                      return option.isNew ? (
                        <CreateNewAutocompleteOption key={key} {...optionProps} option={option} />
                      ) : (
                        <AutocompleteOption
                          key={key}
                          {...optionProps}
                          option={option}
                          dense
                          descriptionContent={
                            option.isExpired ? (
                              <>
                                {option.partnerName} · <ValidityText expired>{option.validityText}</ValidityText>
                              </>
                            ) : undefined
                          }
                        />
                      );
                    }}
                    renderInput={params => (
                      <TextField
                        {...params}
                        size="small"
                        inputRef={field.ref}
                        error={Boolean(fieldState.error)}
                        placeholder={t("DynamicForm.MultipleAgreementInput.AgreementPlaceholder")}
                      />
                    )}
                  />
                </FormDialog.Field>
              )}
            />
          </FormDialog.Fields>
        </FormDialog.Content>

        <FormDialog.ActionButtons>
          <FormDialog.CancelButton onClick={onClose} />
          <FormDialog.SubmitButton isSubmitting={submitMutation.isPending}>
            {t("DynamicForm.MultipleAgreementInput.AddButton")}
          </FormDialog.SubmitButton>
        </FormDialog.ActionButtons>
      </FormDialog.Form>
    </FormDialog.Dialog>
  );
};

const NewAgreementNote = () => {
  const { t } = useTranslation();
  return (
    <Box
      component="span"
      sx={{
        display: "flex",
        gap: 1,
        p: 1.5,
        borderRadius: 1,
        backgroundColor: theme => theme.palette.grey["100"],
        color: theme => theme.palette.text.secondary,
        fontSize: theme => theme.typography.pxToRem(12),
      }}
    >
      <InfoIcon size={16} />
      {t("DynamicForm.MultipleAgreementInput.NewAgreement.Note")}
    </Box>
  );
};
